// Reading an OpenAPI document into what the API reference draws.
//
// The document is a PROJECT'S: the latest version of its `api_reference`
// document in Core. When that text is an OpenAPI description in JSON it is
// drawn as a reference — operations, `$ref`s resolved, each body field's rules
// said in a line. When it is not, the screen shows it as the prose it is.
//
// Pure functions, no React, so the reading is tested on its own.

export const METHODS = ['get', 'post', 'put', 'patch', 'delete'];

// The text of a document, as an OpenAPI description if it is one.
//
// JSON only: parsing YAML would mean a dependency for a format nobody has
// stored yet. A text that is not JSON, or JSON that is not an API description
// (no `openapi`/`swagger` and no `paths`), returns null — and the caller shows
// the text instead of an empty reference.
export function parseOpenApi(content) {
    if (typeof content !== 'string') return null;
    const text = content.trim();
    if (!text.startsWith('{')) return null;
    try {
        const doc = JSON.parse(text);
        const versioned = typeof doc?.openapi === 'string' || typeof doc?.swagger === 'string';
        return versioned && doc.paths && typeof doc.paths === 'object' ? doc : null;
    } catch {
        return null;
    }
}

const CREDENTIAL_LABEL = {
    sessionCookie: 'Sesión',
    agentToken: 'Token de agente',
};

// The prefix every path repeats (`/api/v1`), found rather than assumed: each
// project versions its API its own way. Shown once in the header instead of on
// every row, where it is noise. Empty when the paths share none.
export function commonPrefix(paths) {
    const list = [...paths];
    if (list.length < 2) return '';
    const split = list.map(path => path.split('/'));
    const shared = [];
    for (let i = 0; i < split[0].length; i += 1) {
        const segment = split[0][i];
        if (segment.includes('{') || !split.every(parts => parts[i] === segment)) break;
        shared.push(segment);
    }
    // Never eat a whole path: `/v2/users` next to `/v2/users/{id}` leaves `/v2`,
    // so `/users` still reads as a route.
    while (shared.length > 1 && list.some(path => path === shared.join('/'))) shared.pop();
    return shared.length > 1 ? shared.join('/') : '';
}

export function shortPath(path, prefix = '') {
    return prefix && path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path;
}

// `#/components/schemas/Error` → the schema. Unknown refs come back as they are
// rather than as nothing: a field whose type cannot be read is still a field.
export function resolveRef(spec, schema, seen = new Set()) {
    if (!schema || typeof schema !== 'object' || !schema.$ref) return schema;
    if (seen.has(schema.$ref)) return schema;
    const parts = schema.$ref.replace(/^#\//, '').split('/');
    let node = spec;
    for (const part of parts) node = node?.[part];
    if (!node) return schema;
    seen.add(schema.$ref);
    return resolveRef(spec, node, seen);
}

function credentialsOf(operation) {
    const security = Array.isArray(operation.security) ? operation.security : [];
    const names = [...new Set(security.flatMap(entry => Object.keys(entry ?? {})))];
    return names.map(name => CREDENTIAL_LABEL[name] ?? name);
}

export function typeOf(schema) {
    if (!schema) return '—';
    const type = Array.isArray(schema.type) ? schema.type : schema.type ? [schema.type] : [];
    if (!type.length) {
        if (schema.$ref) return schema.$ref.split('/').pop();
        if (schema.enum) return 'enum';
        return 'cualquiera';
    }
    const base = type.map(item => (
        item === 'array' && schema.items?.type ? `${schema.items.type}[]` : item
    ));
    const formatted = schema.format && base.length === 1 ? `${base[0]} (${schema.format})` : base.join(' | ');
    return formatted;
}

// One line with the rules a field carries, in the words api-reference.md uses.
export function rulesOf(schema) {
    if (!schema) return '';
    const rules = [];
    if (schema.minLength != null) rules.push(`mín. ${schema.minLength}`);
    if (schema.maxLength != null) rules.push(`máx. ${schema.maxLength}`);
    if (schema.minimum != null) rules.push(`≥ ${schema.minimum}`);
    if (schema.exclusiveMinimum != null) rules.push(`> ${schema.exclusiveMinimum}`);
    if (schema.maximum != null) rules.push(`≤ ${schema.maximum}`);
    if (schema.exclusiveMaximum != null) rules.push(`< ${schema.exclusiveMaximum}`);
    if (schema.minItems != null) rules.push(`mín. ${schema.minItems} elementos`);
    if (schema.maxItems != null) rules.push(`máx. ${schema.maxItems} elementos`);
    if (Array.isArray(schema.enum)) rules.push(`uno de: ${schema.enum.map(value => `\`${value}\``).join(', ')}`);
    if (schema.pattern) rules.push(`patrón \`${schema.pattern}\``);
    if (schema.default !== undefined) rules.push(`por defecto \`${JSON.stringify(schema.default)}\``);
    return rules.join(', ');
}

// The fields of an object schema, required first in the order they were
// declared. A body that is not an object (rare) is one row named «(cuerpo)».
export function fieldsOf(spec, schema) {
    const resolved = resolveRef(spec, schema);
    if (!resolved) return [];

    if (resolved.type !== 'object' || !resolved.properties) {
        return [{
            name: '(cuerpo)',
            type: typeOf(resolved),
            required: true,
            rules: rulesOf(resolved),
            description: resolved.description ?? '',
        }];
    }

    const required = new Set(Array.isArray(resolved.required) ? resolved.required : []);
    return Object.entries(resolved.properties).map(([name, raw]) => {
        const field = resolveRef(spec, raw);
        return {
            name,
            type: typeOf(field),
            required: required.has(name),
            rules: rulesOf(field),
            description: field?.description ?? '',
        };
    });
}

export function readOperations(spec) {
    if (!spec || typeof spec !== 'object' || !spec.paths) return [];

    const prefix = commonPrefix(Object.keys(spec.paths));
    const operations = [];
    for (const [path, item] of Object.entries(spec.paths)) {
        for (const method of METHODS) {
            const operation = item?.[method];
            if (!operation) continue;

            const tag = operation.tags?.[0] ?? 'Sin grupo';
            const body = operation.requestBody?.content?.['application/json']?.schema ?? null;

            operations.push({
                id: operation.operationId ?? `${method}-${path}`,
                method,
                path,
                short: shortPath(path, prefix),
                tag,
                summary: operation.summary ?? '',
                description: operation.description ?? '',
                permission: operation['x-mm-permission'] ?? null,
                credentials: credentialsOf(operation),
                parameters: (operation.parameters ?? []).map(raw => {
                    const parameter = resolveRef(spec, raw);
                    return {
                        name: parameter.name,
                        in: parameter.in,
                        required: !!parameter.required,
                        type: typeOf(parameter.schema),
                        description: parameter.description ?? '',
                    };
                }),
                body: body ? fieldsOf(spec, body) : null,
                bodyExample: body ? exampleOf(spec, body) : null,
                responses: Object.entries(operation.responses ?? {}).map(([code, response]) => ({
                    code,
                    description: resolveRef(spec, response)?.description ?? '',
                })),
            });
        }
    }
    return operations;
}

// A small example body, like the ones api-reference.md prints. Values are
// placeholders by type — never real data.
export function exampleOf(spec, schema, depth = 0) {
    const resolved = resolveRef(spec, schema);
    if (!resolved || depth > 4) return null;
    if (resolved.example !== undefined) return resolved.example;
    if (Array.isArray(resolved.enum) && resolved.enum.length) return resolved.enum[0];
    if (resolved.default !== undefined) return resolved.default;

    const type = Array.isArray(resolved.type) ? resolved.type.find(item => item !== 'null') : resolved.type;
    switch (type) {
    case 'object': {
        const out = {};
        for (const [name, field] of Object.entries(resolved.properties ?? {})) {
            out[name] = exampleOf(spec, field, depth + 1);
        }
        return out;
    }
    case 'array':
        return resolved.items ? [exampleOf(spec, resolved.items, depth + 1)] : [];
    case 'integer':
    case 'number':
        return Math.max(1, resolved.minimum ?? resolved.exclusiveMinimum ?? 1);
    case 'boolean':
        return true;
    case 'string':
        return resolved.format === 'date-time' ? '2026-01-01T00:00:00.000Z' : 'texto';
    default:
        return null;
    }
}

// The group a tag belongs to: `Admin/Planeación` → `Admin`. Tags without a
// slash are their own group.
export function groupOf(tag) {
    const index = tag.indexOf('/');
    return index === -1 ? tag : tag.slice(0, index);
}

export function tagLabel(tag) {
    const index = tag.indexOf('/');
    return index === -1 ? tag : tag.slice(index + 1);
}

// Search over what a person would type: the path, the summary, the permission,
// the operation id. Accents and case do not matter — «planeacion» finds
// «Planeación».
const fold = text => String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export function filterOperations(operations, { q = '', method = '', tag = '' } = {}) {
    const needle = fold(q.trim());
    return operations.filter(operation => {
        if (method && operation.method !== method) return false;
        if (tag && operation.tag !== tag) return false;
        if (!needle) return true;
        return [operation.path, operation.summary, operation.permission, operation.id, operation.tag]
            .some(value => fold(value).includes(needle));
    });
}

// Tags in the order the spec declares them, each with its operations; tags the
// spec forgot to declare go at the end rather than disappear.
export function groupByTag(spec, operations) {
    const declared = (spec?.tags ?? []).map(entry => entry.name);
    const byTag = new Map();
    for (const operation of operations) {
        if (!byTag.has(operation.tag)) byTag.set(operation.tag, []);
        byTag.get(operation.tag).push(operation);
    }
    const order = [...declared.filter(name => byTag.has(name)), ...[...byTag.keys()].filter(name => !declared.includes(name))];
    return order.map(name => ({ tag: name, operations: byTag.get(name) }));
}
